import { useState, useEffect, useRef } from "react";
import fetchWithCredentials from "apiClient";
import Message from "types/Message";

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export function useChat(
  teacherId: number | null,
  studentId: number | null,
  page: number
): {
  messages: Message[];
  loading: boolean;
  error: string | null;
  next: string | null;
  sendMessage: (content: string, fileUrl?: string | null) => Promise<null>;
} {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [next, setNext] = useState<string | null>(null);

  const previousStudentIdRef = useRef<number | null>(null);
  
  useEffect(() => {
    if (!teacherId || !studentId) {
      setLoading(false);
      return;
    }

    // Check if studentId or page changed
    const studentIdChanged = previousStudentIdRef.current !== studentId;
    
    // Update refs
    previousStudentIdRef.current = studentId;
    
    if (studentIdChanged) {
      setMessages([]);
      setNext(null);
      setError(null);
    }

    // Always use page 1 when studentId changes, otherwise use the provided page
    const pageToFetch = studentIdChanged ? 1 : page;
    
    const fetchMessages = async () => {
      setLoading(true);
      setError(null);
      try {
        const data: PaginatedResponse<Message> = await fetchWithCredentials(
          `/api/teacher/students/${studentId}/chat/?page=${pageToFetch}`
        );
        setMessages(data.results);
        setNext(data.next);
      } catch (err: any) {
        console.error('[useChat] Error fetching messages', err);
        const errorMessage = err.message || "Error fetching chat messages";
        // If it's a 404 and we're not on page 1, silently handle it (student has fewer pages)
        if (errorMessage.includes("404") && pageToFetch > 1) {
          // Student doesn't have this many pages, just set empty results
          setMessages([]);
          setNext(null);
        } else {
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    // Always fetch when effect runs (on mount or when dependencies change)
    fetchMessages();
  }, [teacherId, studentId, page]);

  const sendMessage = async (
    content: string,
    fileUrl?: string | null
  ): Promise<null> => {
    const body: any = { content };
    if (fileUrl) body.fileUrl = fileUrl;
    fetchWithCredentials(`/api/teacher/students/${studentId}/chat/`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    return null;
  };

  return { messages, loading, error, next, sendMessage };
}
